import { getFontMetrics } from "../lib/FontMetrics";
import { getSpacingBottom, getSpacingTop } from "../Box/spacing";
// ----------------------------------------------------------------------------
// Utility methods
// ----------------------------------------------------------------------------
export const getHeight = ({
  lineHeight,
  padding
}) => {
  return lineHeight + getSpacingTop(padding) + getSpacingBottom(padding);
};

// ----------------------------------------------------------------------------
// Style generation
// ----------------------------------------------------------------------------
// Compute the position's distance from an alignment target's top edge.
const distanceFromTopEdge = (props, position) => {
  const {
    size,
    typeface
  } = props;
  const height = getHeight(props);
  const font = getFontMetrics(typeface || 'base');
  if (!size) {
    // When a size isn't provided, we assume the top of the element is the cap
    // height and the bottom is the baseline. This allows us to position
    // components like icons and images.
    switch (position) {
      case 'baseline':
      case 'descender':
      case 'capHeight':
        return height;
      case 'capCenter':
        return 0.5 * height;
      case 'xHeight':
      case 'xCenter':
        const multiplier = (font.position.baseline - font.position[position]) / (font.position.baseline - font.position.capHeight);
        return (1 - multiplier) * height;
      default:
        return 0;
    }
  }
  return font.positionFor(position, props);
};

// For a given position, compute the difference between a target and
// reference's distance from its top edge.
const differenceBetweenPositions = (target, reference, position, targetPosition) => {
  if (getHeight(reference) === 0) {
    return 0;
  }
  return distanceFromTopEdge(reference, position) - distanceFromTopEdge(target, targetPosition || position);
};

// For some strategies (e.g. x-height) the translation should not shift the
// target below the reference's baseline. If the translation is below the
// reference's baseline, translate to the reference's baseline instead.
const minBaseline = (target, reference, translation) => {
  return Math.min(translation, differenceBetweenPositions(target, reference, 'baseline'));
};

// Compute the translation for a target based on the provided reference and
// the target's built-in strategy.
const translationForStrategy = (target, reference) => {
  switch (target.strategy) {
    case 'baseline':
      return differenceBetweenPositions(target, reference, 'baseline');
    case 'center':
      return differenceBetweenPositions(target, reference, 'capCenter');
    case 'x-center':
      const {
        size
      } = reference;
      const targetHeight = getHeight(target);
      const heightDiff = size - targetHeight;
      // When reference text size is larger than target height (including padding),
      // align the target's capCenter with reference's xCenter.
      // Otherwise, align the capCenters, but ensure target's baseline is never
      // below the reference baseline.
      const position = heightDiff <= 2 ? 'capCenter' : 'xCenter';
      const xCenter = differenceBetweenPositions(target, reference, position, 'capCenter');
      return minBaseline(target, reference, xCenter);
    case 'x-height':
      const xHeight = differenceBetweenPositions(target, reference, 'xHeight', 'capHeight');
      return minBaseline(target, reference, xHeight);
    default:
      return 0;
  }
};

// Convert an origin into a corresponding font position.
const originToFontPosition = origin => {
  switch (origin) {
    case 'baseline':
      return 'baseline';
    default:
      return null;
  }
};

// Browsers do not render text the same way. The text rendering
// specification[0] says nothing about rounding, so everyone does their own
// thing.
//
// Some browser engines use floating point arithmetic when computing font
// metrics while others just use integers. Floating point engines (like the
// ones in Firefox) render text more precisely than integer engines (like
// Blink and WebKit).
//
// The spec says to add half of the leading above the content box and the other
// half below. It doesn’t specify what to do when the leading can’t be evenly
// split, so browsers make their own way. Blink and WebKit put the extra
// leading below the content area. Firefox puts the extra leading above the
// content area.
//
// We detect and correct for the half-pixel error in Blink and WebKit to ensure
// aligned components consistently align with non-aligned centered elements.
// Firefox's margin of error is less than half a pixel, so we use native
// rendering in those cases.
//
// [0] https://www.w3.org/TR/CSS2/visudet.html#line-height
export const splitPixels = false; // TODO(koop): Check if we need this with system fonts
// typeof navigator === 'undefined'
//   ? false
//   : !!navigator.userAgent.match(/AppleWebKit/i);

const getSplitPixelOffset = (target, reference) => {
  const {
    lineHeight,
    size,
    splitPixels,
    typeface
  } = reference;
  if (!splitPixels || !size || target.nestable) {
    return 0;
  }
  const font = getFontMetrics(typeface || 'base');
  const ascender = font.configFor('ascender', size);
  const descender = font.configFor('descender', size);
  const contentArea = Math.round(ascender) + Math.round(Math.abs(descender));
  return (Math.round(lineHeight) - contentArea) % 2 ? 0.5 : 0;
};

// Format a number as a pixel string, rounding it to two decimal places.
const px = n => `${Math.round(n * 100) / 100}px`;

// Generate the necessary styles to align the target with the reference.
//
// We use two strategies here:
//
// 1. Margins - Match the target’s line height with the reference line height.
//
// We use margins to adjust the size of the element (while still allowing
// text targets to wrap).
//
// 2. Transforms - Shift the target to an optically aligned position.
//
// We use CSS transforms to shift the target without affecting the element's
// bounding box.
export const getStyle = (target, reference) => {
  if (!reference.size && !reference.lineHeight) {
    return {};
  }
  const {
    origin
  } = reference;
  const isBaselineOrigin = origin === 'baseline';
  const originPosition = originToFontPosition(origin);
  let originDiff = 0;
  if (originPosition) {
    originDiff = differenceBetweenPositions(target, reference, originPosition);
  }
  const heightDiff = getHeight(reference) - getHeight(target);
  const marginTop = originDiff;
  const marginBottom = heightDiff - marginTop;
  const translateY = translationForStrategy(target, reference) - marginTop + getSplitPixelOffset(target, reference);
  const style = {};
  if (isBaselineOrigin) {
    // Since AlignBox renders a node that ensures the first line is at
    // least the height of the provided reference, when items are aligned on the
    // baseline we can bias toward attempting to ensure the first line does not
    // exceed the height of the reference due to browser baseline alignment
    // rounding errors.
    //
    // Account for up to one pixel of browser misalignment by subtracting an
    // extra pixel from the top margin.
    const baselineMarginTop = marginTop - 1;

    // Only apply margins when they're negative to avoid accidentally
    // increasing the first line's height.
    if (baselineMarginTop < 0) {
      style.marginTop = px(baselineMarginTop);
    }
    if (marginBottom < 0) {
      style.marginBottom = px(marginBottom);
    }
  } else {
    // Since the target's origin is at the top of the element, we have to add
    // all margins to ensure it's properly aligned.
    if (marginTop) {
      style.marginTop = px(marginTop);
    }
    if (marginBottom) {
      style.marginBottom = px(marginBottom);
    }
  }
  if (translateY) {
    style.transform = `translateY(${px(translateY)})`;
  }
  return style;
};